import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://server.havbit.in/api';

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const storedUser = localStorage.getItem('havbit_user');
        const storedToken = localStorage.getItem('havbit_token');

        if (storedUser && storedToken) {
            try {
                setUser(JSON.parse(storedUser));
                setIsAuthenticated(true);
            } catch (error) {
                console.error('Error parsing stored user:', error);
                localStorage.removeItem('havbit_user');
                localStorage.removeItem('havbit_token');
            }
        }

        setLoading(false);
    }, []);

    const normalizeUserData = (backendUser, token) => ({
        _id: backendUser._id || backendUser.id,
        id: backendUser.id || backendUser._id,
        fullName: backendUser.fullName || backendUser.name || '',
        name: backendUser.name || backendUser.fullName || '',
        email: backendUser.email || '',
        phone: backendUser.phone || '',
        profilePicture: backendUser.profilePicture || '',
        token,
        authProvider: backendUser.authProvider || 'local'
    });

    const saveSession = (backendUser, token) => {
        const normalizedUser = normalizeUserData(backendUser, token);
        localStorage.setItem('havbit_user', JSON.stringify(normalizedUser));
        localStorage.setItem('havbit_token', token);
        setUser(normalizedUser);
        setIsAuthenticated(true);
    };

    const register = async (formData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/website-user/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fullName: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    password: formData.password,
                    confirmPassword: formData.confirmPassword
                })
            });

            const data = await response.json();
            return {
                success: response.ok && data.success,
                message: data.message || (response.ok ? 'Registration successful' : 'Registration failed')
            };
        } catch (error) {
            console.error('Registration error:', error);
            return { success: false, message: 'Network error. Please try again.' };
        }
    };

    const login = async (email, password) => {
        try {
            const response = await fetch(`${API_BASE_URL}/website-user/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                return { success: false, message: data.message || 'Login failed' };
            }

            saveSession(data.user, data.token);
            return { success: true };
        } catch (error) {
            console.error('Login error:', error);
            return { success: false, message: 'Network error. Please try again.' };
        }
    };

    const loginWithGoogle = async (credential) => {
        if (!credential) {
            return { success: false, message: 'Google did not return an identity token.' };
        }

        try {
            const response = await fetch(`${API_BASE_URL}/website-user/google-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                return { success: false, message: data.message || 'Google login failed' };
            }

            saveSession(data.user, data.token);
            return { success: true };
        } catch (error) {
            console.error('Google login error:', error);
            return { success: false, message: 'Network error. Please try again.' };
        }
    };

    const logout = () => {
        localStorage.removeItem('havbit_user');
        localStorage.removeItem('havbit_token');
        setUser(null);
        setIsAuthenticated(false);
    };

    const sendPhoneOtp = async (phone) => {
        try {
            const response = await fetch(`${API_BASE_URL}/website-user/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone })
            });
            const data = await response.json();
            return {
                success: response.ok && data.success,
                message: data.message || (response.ok ? 'OTP sent' : 'Failed to send OTP')
            };
        } catch (error) {
            console.error('Send OTP error:', error);
            return { success: false, message: 'Network error. Please try again.' };
        }
    };

    const verifyPhoneOtp = async (phone, otp, fullName) => {
        try {
            const response = await fetch(`${API_BASE_URL}/website-user/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone, otp, fullName })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                return { success: false, message: data.message || 'OTP verification failed' };
            }
            saveSession(data.user, data.token);
            return { success: true };
        } catch (error) {
            console.error('Verify OTP error:', error);
            return { success: false, message: 'Network error. Please try again.' };
        }
    };

    const getUserId = () => user?._id || user?.id || null;
    const getToken = () => localStorage.getItem('havbit_token');

    const value = {
        user,
        isAuthenticated,
        loading,
        register,
        login,
        loginWithGoogle,
        sendPhoneOtp,
        verifyPhoneOtp,
        logout,
        getUserId,
        getToken
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
